import React, { useState, useEffect, useRef, useCallback } from 'react';
import { bannersAPI, API_BASE } from '@/lib/api';
import { X, Volume2, VolumeX, ChevronLeft, ChevronRight } from 'lucide-react';

const BannerCRM = () => {
  const [banners, setBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fechado, setFechado] = useState(false);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    carregarBanners();
  }, []);

  // Auto-rotate carousel every 8 seconds
  useEffect(() => {
    if (banners.length <= 1) return;
    intervalRef.current = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % banners.length);
    }, 8000);
    return () => clearInterval(intervalRef.current);
  }, [banners.length]);

  const carregarBanners = async () => {
    try {
      const response = await bannersAPI.listar();
      const ativos = (response.data || []).filter(b => {
        if (!b.ativo) return false;
        const hoje = new Date();
        const inicio = new Date(b.data_inicio);
        const fim = new Date(b.data_fim);
        fim.setHours(23, 59, 59);
        return hoje >= inicio && hoje <= fim;
      });
      if (ativos.length > 0) {
        setBanners(ativos);
        setFechado(false);
      }
    } catch (error) {
      console.error('Erro ao carregar banners:', error);
    }
  };

  const goTo = useCallback((idx) => {
    setCurrentIndex(idx);
    // Reset timer
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (banners.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % banners.length);
      }, 8000);
    }
  }, [banners.length]);

  const goPrev = () => goTo((currentIndex - 1 + banners.length) % banners.length);
  const goNext = () => goTo((currentIndex + 1) % banners.length);

  const getMediaUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api')) return `${API_BASE.replace('/api', '')}${url}`;
    return url;
  };

  const getYoutubeEmbedId = (url) => {
    if (!url) return null;
    const match = url.match(/^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/);
    if (match && match[7]?.length === 11) return match[7];
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return { type: 'vimeo', id: vimeoMatch[1] };
    return null;
  };

  const toggleMute = () => {
    setMuted(prev => {
      const next = !prev;
      if (videoRef.current) videoRef.current.muted = next;
      return next;
    });
  };

  if (!banners.length || fechado) return null;

  const banner = banners[currentIndex] || banners[0];
  const tipo = banner.tipo_midia || 'nenhum';
  const hasVideo = tipo === 'video_upload' || tipo === 'video_youtube';
  const hasMedia = tipo !== 'nenhum';

  const renderMedia = () => {
    if (tipo === 'imagem' && banner.imagem_url) {
      return (
        <img 
          src={getMediaUrl(banner.imagem_url)} 
          alt="Banner" 
          className="h-full w-auto max-w-[65%] object-contain rounded-lg"
          onError={(e) => e.target.style.display = 'none'}
        />
      );
    }
    if (tipo === 'video_upload' && banner.video_url) {
      return (
        <video
          ref={videoRef}
          src={getMediaUrl(banner.video_url)}
          className="h-full w-auto max-w-[65%] object-contain rounded-lg"
          autoPlay loop muted={muted} playsInline
          data-testid="banner-video"
        />
      );
    }
    if (tipo === 'video_youtube' && banner.video_youtube) {
      const embedId = getYoutubeEmbedId(banner.video_youtube);
      if (!embedId) return null;
      if (typeof embedId === 'object' && embedId.type === 'vimeo') {
        return (
          <iframe
            src={`https://player.vimeo.com/video/${embedId.id}?autoplay=1&muted=${muted ? 1 : 0}&loop=1&background=1`}
            className="h-full aspect-video rounded-lg"
            allow="autoplay; fullscreen" frameBorder="0" title="Vimeo"
          />
        );
      }
      return (
        <iframe
          src={`https://www.youtube.com/embed/${embedId}?autoplay=1&mute=${muted ? 1 : 0}&loop=1&playlist=${embedId}&controls=0&showinfo=0&rel=0`}
          className="h-full aspect-video rounded-lg"
          allow="autoplay; encrypted-media" frameBorder="0" title="YouTube"
          data-testid="banner-youtube"
        />
      );
    }
    return null;
  };

  return (
    <div 
      className="relative w-full flex items-center justify-center gap-6 shadow-lg overflow-hidden transition-colors duration-500"
      style={{ backgroundColor: banner.cor_fundo || '#DC2626', height: hasMedia ? '160px' : '64px' }}
      data-testid="banner-crm"
    >
      {/* Mídia */}
      {renderMedia()}
      
      {/* Textos */}
      <div className="flex flex-col items-center text-white shrink-0 z-10">
        <p className="text-xl font-bold drop-shadow" data-testid="banner-texto-principal">
          {banner.texto_principal}
        </p>
        {banner.texto_secundario && (
          <p className="text-sm opacity-90 drop-shadow" data-testid="banner-texto-secundario">
            {banner.texto_secundario}
          </p>
        )}
      </div>

      {/* Controles do carrossel */}
      {banners.length > 1 && (
        <>
          <button onClick={goPrev} className="absolute left-10 top-1/2 -translate-y-1/2 text-white/60 hover:text-white p-1 rounded-full hover:bg-white/10 transition-colors z-20" data-testid="banner-prev">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button onClick={goNext} className="absolute right-10 top-1/2 -translate-y-1/2 text-white/60 hover:text-white p-1 rounded-full hover:bg-white/10 transition-colors z-20" data-testid="banner-next">
            <ChevronRight className="h-5 w-5" />
          </button>
          {/* Dots */}
          <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1.5 z-20">
            {banners.map((_, i) => (
              <button key={i} onClick={() => goTo(i)}
                className={`w-2 h-2 rounded-full transition-all ${i === currentIndex ? 'bg-white w-4' : 'bg-white/40 hover:bg-white/70'}`}
              />
            ))}
          </div>
        </>
      )}

      {/* Botão de som */}
      {hasVideo && (
        <button onClick={toggleMute}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white p-1.5 rounded-full hover:bg-white/10 transition-colors z-20"
          title={muted ? 'Ativar som' : 'Desativar som'} data-testid="banner-toggle-som"
        >
          {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
        </button>
      )}

      {/* Fechar */}
      <button onClick={() => setFechado(true)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors z-20"
        title="Fechar" data-testid="banner-fechar-btn"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  );
};

export default BannerCRM;
